import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { ImageOff, RefreshCw } from "lucide-react";

/** Fired on window when an image finally fails after its own retries (e.g. an expired signed link). Pages listen and
 *  refetch the job so every <SmartImage> gets a fresh URL. */
export const MEDIA_ERROR_EVENT = "listing-ai:media-error";
const RETRY_DELAYS_MS = [1200, 4000];
const STALL_MS = 25000; // a request that neither loads nor errors within this budget is treated as stalled and retried

/**
 * Image with a shimmer placeholder while loading, automatic retries on transient failures (the object store can stall
 * or a signed link can expire while a tab sits open), a fallback chain (`fallbacks`: e.g. optimised -> original when the
 * thumbnail is missing) and a quiet, clickable fallback. Fills its parent.
 */
export const SmartImage = ({ src, fallbacks = [], alt = "", className, imgClassName, loading = "lazy", testid, onState }) => {
  const sources = useMemo(() => [...new Set([src, ...(fallbacks || [])].filter(Boolean))], [src, fallbacks]);
  const [state, setState] = useState("loading"); // loading | ok | error
  const [step, setStep] = useState({ idx: 0, attempt: 0 }); // which source, which retry of it
  const timer = useRef(null);
  const stall = useRef(null);
  const clear = () => { clearTimeout(timer.current); clearTimeout(stall.current); };
  useEffect(() => { setState("loading"); setStep({ idx: 0, attempt: 0 }); return clear; }, [sources.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onState?.(state); }, [state, onState]);

  const current = sources[step.idx];
  const effectiveSrc = current ? (step.attempt === 0 ? current : `${current}${current.includes("?") ? "&" : "?"}r=${step.attempt}`) : null;

  // stall guard: some loads hang without ever firing load/error (dropped connection behind a proxy)
  useEffect(() => {
    if (state !== "loading" || !effectiveSrc) return undefined;
    stall.current = setTimeout(() => setStep((s) => ({ ...s, attempt: s.attempt + 1 })), STALL_MS);
    return () => clearTimeout(stall.current);
  }, [state, effectiveSrc]);

  if (!sources.length) return <div className={cn("h-full w-full bg-muted flex items-center justify-center text-muted-foreground", className)} data-testid={testid}><ImageOff className="h-4 w-4" /></div>;

  const onError = () => {
    clearTimeout(stall.current);
    if (step.attempt < RETRY_DELAYS_MS.length) {
      timer.current = setTimeout(() => { setStep((s) => ({ ...s, attempt: s.attempt + 1 })); setState("loading"); }, RETRY_DELAYS_MS[step.attempt]);
      return;
    }
    if (step.idx < sources.length - 1) { setStep({ idx: step.idx + 1, attempt: 0 }); setState("loading"); return; }
    setState("error");
    window.dispatchEvent(new CustomEvent(MEDIA_ERROR_EVENT, { detail: { src } }));
  };
  const retry = (e) => { e.stopPropagation(); setStep({ idx: 0, attempt: step.attempt + 1 }); setState("loading"); };
  return (
    <div className={cn("relative h-full w-full", className)} data-testid={testid} data-state={state}>
      {state === "loading" && <div className="absolute inset-0 shimmer" aria-hidden="true" />}
      {state === "error" && (
        <button type="button" onClick={retry} className="absolute inset-0 bg-muted flex flex-col items-center justify-center gap-1 text-muted-foreground text-[11px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="smart-image-retry" aria-label="Last bildet på nytt">
          <RefreshCw className="h-4 w-4" />
          <span>Last på nytt</span>
        </button>
      )}
      <img key={effectiveSrc} src={effectiveSrc} alt={alt} loading={loading} decoding="async" onLoad={() => { clearTimeout(stall.current); setState("ok"); }} onError={onError}
        className={cn("h-full w-full object-cover transition-opacity duration-200", state === "ok" ? "opacity-100" : "opacity-0", imgClassName)} />
    </div>
  );
};
